import { WidgetType } from 'types/widget.types';

import { DEFAULT_BG, DEFAULT_FG, DEFAULT_SIZE_HEIGHT_PX, DEFAULT_SIZE_WIDTH_PX } from './VideoEmbedWidget.config';
import { VERSION } from './VideoEmbedWidget.upgrade';

export const IFRAMELY_KEY = process.env.REACT_APP_IFRAMELY_TOKEN ?? '';

/**
 * Generate default video widget data
 *
 * @param url - Video URL
 * @param topPxOverride - top position override
 * @param leftPxOverride - left position override
 * @returns {Object} default video widget data
 */
const generateVideoEmbedDefaultData = (url: string, topPxOverride?: number, leftPxOverride?: number) => {
  const widget = {
    widgetType: WidgetType.VideoEmbed,
    widgetData: {
      version: VERSION,
      topPx: topPxOverride || 0,
      leftPx: leftPxOverride || 0,
      rotateDeg: 0,
      url,
      widthPx: DEFAULT_SIZE_WIDTH_PX,
      heightPx: DEFAULT_SIZE_HEIGHT_PX,
      foreGroundColor: DEFAULT_FG,
      backgroundColor: DEFAULT_BG,
      altText: `Video ${url}`,
      isDecorative: false,
    },
  };

  return widget;
};

/**
 * Get the video ID based on a YT URL
 *
 * @param youtubeVideoUrl YT url
 * @returns {String} YT video id
 */
const getVideoIdFromYoutubeVideoUrl = (youtubeVideoUrl: string) => {
  let videoId = '';
  const array = youtubeVideoUrl.match(
    /^.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]*).*/,
  );
  if (array != null) {
    videoId = array[1].replace(/\?.*/, '') as string;
  }
  return videoId;
};

/**
 * Get the video ID based on a TikTok URL
 *
 * @param url TikTok url
 * @returns {String} TikTok video id
 */
export const getTikTokId = (url: string): string => {
  // Parse basic url and embeds
  const basicReg = /tiktok\.com(.*)\/video\/(\d+)/gm;
  const basicParsed = basicReg.exec(url);
  if (basicParsed && basicParsed.length > 2) {
    return basicParsed[2];
  }

  return '';
};

/**
 * Get youtube cover thumbnail url
 *
 * @param {String} video_id, youtube video id
 * @param {Boolean} max, if true, use 16:9 max resolution default thumbnail
 * @return {String} youtube, cover image url
 */
const getYoutubeImageFromId = (videoId: string, max = false) => {
  return `https://img.youtube.com/vi/${videoId}${max ? '/maxresdefault.jpg' : '/sddefault.jpg'}`;
};

type IframelyDimension = {
  links: {
    thumbnail: [{ media: { height: number; width: number } }];
  };
};

/**
 * Iframely returns a width and height from the video, we'll use this dimension
 * to create the widget so we have a widget that fits the size
 *
 * @param url video URL
 * @returns IframelyDimension object
 */
export async function getIframelyDimension(url: string): Promise<IframelyDimension> {
  const resp = await fetch(`https://iframe.ly/api/iframely?url=${encodeURIComponent(url)}&api_key=${IFRAMELY_KEY}`, {
    method: 'GET',
  });

  if (!resp.ok) {
    throw new Error('Failed to get dimension');
  }

  return await resp.json();
}

/**
 * List with the supported video domains
 */
const videoDomains = ['yout', 'youtube', 'vimeo', 'giphy', 'tiktok', 'loom'];

/**
 * Check if the URL is allowed as a supported video URL
 *
 * @param url video url
 * @returns {Boolean} returns if a URL is a allowed video URL
 */
const isVideoLink = (url: string) => {
  if (!url) return false;

  return videoDomains.some((domain) => {
    if (url.includes(domain)) {
      return true;
    }
    return false;
  });
};

/**
 * Check if the URL is allowed as a supported GIPHY URL
 *
 * @param url GIPHY url
 * @returns {Boolean} returns if a URL is a allowed GIPHY URL
 */
const isGIFLink = (url: string) => {
  if (!url) return false;

  if (url.includes('giphy')) {
    return true;
  }
  return false;
};
interface VideoDimension {
  width: number;
  height: number;
}

export const YTDimension = {
  width: 390,
  height: 219,
};

export const YTShortsDimension = {
  width: 200,
  height: 360,
};

export const TikTokShortsDimension = {
  width: 324,
  height: 560,
};

/**
 * For YT it returns a default dimension
 * For other video players we get the dimensions from Iframely
 *
 * @param url video url
 * @returns VideoDimension width and height based on the video type
 */
export const getDefaultVideoDimensions = async (url: string): Promise<VideoDimension> => {
  const isYT = url.includes('youtu') && !url.includes('shorts');
  const isYTShorts = url.includes('youtu') && url.includes('shorts');
  const isTikTok = url.includes('tiktok');
  const isVimeo = url.includes('vimeo');
  const isLoom = url.includes('loom');

  if (isYT || isLoom || isVimeo) return YTDimension;
  if (isYTShorts) return YTShortsDimension;
  if (isTikTok) return TikTokShortsDimension;

  const dimension = await getIframelyDimension(url);
  if (dimension?.links?.thumbnail?.[0]?.media) {
    return {
      width: Math.min(...dimension?.links?.thumbnail.map((item) => item.media.width)),
      height: Math.min(...dimension?.links?.thumbnail.map((item) => item.media.height)),
    };
  }

  // fallback
  return YTDimension;
};

export { generateVideoEmbedDefaultData, getVideoIdFromYoutubeVideoUrl, getYoutubeImageFromId, isVideoLink, isGIFLink };
